import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Button,
  useMediaQuery,
} from "@mui/material";
import ReactCountryFlag from "react-country-flag";

// Language data with flag mappings
const languages = [
  { name: "Afrikaans", flag: "za" },
  { name: "Albanska", flag: "al" },
  { name: "Amhariska", flag: "et" },
  { name: "Arabiska", flag: "sa" },
  { name: "Armeniska", flag: "am" },
  { name: "Assyriska", flag: "iq" },
  { name: "Azerbajdzjanska", flag: "az" },
  { name: "Bengali", flag: "bd" },
  { name: "Berber", flag: "ma" },
  { name: "Bosniska", flag: "ba" },
  { name: "Brasiliansk portugisiska", flag: "br" },
  { name: "Bulgariska", flag: "bg" },
  { name: "Burmesiska", flag: "mm" },
  { name: "Danska", flag: "dk" },
  { name: "Dari", flag: "af" },
  { name: "Egyptisk arabiska", flag: "eg" },
  { name: "Engelska", flag: "gb" },
  { name: "Estniska", flag: "ee" },
  { name: "Farsi", flag: "ir" },
  { name: "Finska", flag: "fi" },
  { name: "Franska", flag: "fr" },
  { name: "Georgiska", flag: "ge" },
  { name: "Grekiska", flag: "gr" },
  { name: "Gujarati", flag: "in" },
  { name: "Havasa", flag: "ng" },
  { name: "Hebreiska", flag: "il" },
  { name: "Hindi", flag: "in" },
  { name: "Igbo", flag: "ng" },
  { name: "Indonesiska", flag: "id" },
  { name: "Isländska", flag: "is" },
  { name: "Italienska", flag: "it" },
  { name: "Japanska", flag: "jp" },
  { name: "Kabyliska", flag: "dz" },
  { name: "Kannada", flag: "in" },
  { name: "Karen", flag: "mm" },
  { name: "Kazakiska", flag: "kz" },
  { name: "Khmer", flag: "kh" },
  { name: "Kinesiska", flag: "cn" },
  { name: "Koreanska", flag: "kr" },
  { name: "Kroatiska", flag: "hr" },
  { name: "Kurdiska", flag: "iq" },
  { name: "Kurmanji", flag: "tr" },
  { name: "Lettiska", flag: "lv" },
  { name: "Litauiska", flag: "lt" },
  { name: "Maghrebi arabiska", flag: "ma" },
  { name: "Makedonska", flag: "mk" },
  { name: "Malagasy", flag: "mg" },
  { name: "Malayalam", flag: "in" },
  { name: "Maltesiska", flag: "mt" },
  { name: "Marathi", flag: "in" },
  { name: "Modern standardarabiska", flag: "sa" },
  { name: "Mongoliska", flag: "mn" },
  { name: "Nederländska", flag: "nl" },
  { name: "Nepali", flag: "np" },
  { name: "Norska", flag: "no" },
  { name: "Pashtu", flag: "af" },
  { name: "Persiska", flag: "ir" },
  { name: "Polska", flag: "pl" },
  { name: "Portugisiska", flag: "pt" },
  { name: "Punjabi", flag: "in" },
  { name: "Rumänska", flag: "ro" },
  { name: "Ryska", flag: "ru" },
  { name: "Serbiska", flag: "rs" },
  { name: "Sinhala", flag: "lk" },
  { name: "Slovakiska", flag: "sk" },
  { name: "Slovenska", flag: "si" },
  { name: "Somali", flag: "so" },
  { name: "Sorani", flag: "iq" },
  { name: "Spanska", flag: "es" },
  { name: "Svenska", flag: "se" },
  { name: "Swahili", flag: "tz" },
  { name: "Tachelhit", flag: "ma" },
  { name: "Tagalog", flag: "ph" },
  { name: "Tamil", flag: "in" },
  { name: "Telugu", flag: "in" },
  { name: "Thai", flag: "th" },
  { name: "Thai-Lao", flag: "la" },
  { name: "Tibetanska", flag: "cn" },
  { name: "Tigrinja", flag: "er" },
  { name: "Tjeckiska", flag: "cz" },
  { name: "Turkiska", flag: "tr" },
  { name: "Tyska", flag: "de" },
  { name: "Uiguriska", flag: "cn" },
  { name: "Ukrainska", flag: "ua" },
  { name: "Ungerska", flag: "hu" },
  { name: "Urdu", flag: "pk" },
  { name: "Uzbekiska", flag: "uz" },
  { name: "Vietnamesiska", flag: "vn" },
  { name: "Wolof", flag: "sn" },
  { name: "Xhosa", flag: "za" },
  { name: "Yoruba", flag: "ng" },
  { name: "Zulu", flag: "za" },
];

const OurLanguages = ({
  theme,
  styles,
  title = "Språk vi stödjer",
  subtitle = "När du genererar innehållet kan välja mellan följande språk.",
}) => {
  const isMobile = true;
  const [showAll, setShowAll] = useState(false);

  const MOBILE_LIMIT = 30;
  const displayedLanguages =
    isMobile && !showAll ? languages.slice(0, MOBILE_LIMIT) : languages;

  const hasMoreLanguages = isMobile && languages.length > MOBILE_LIMIT;

  return (
    <Container maxWidth="xl" sx={{ mb: { xs: 5, md: 20 }, maxWidth: "1300px" }}>
      <Typography
        variant="h2"
        sx={{
          fontWeight: 800,
          mb: 2,
          fontSize: { xs: "2rem", md: "2.8rem" },
          textAlign: "center",
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="subtitle"
        sx={{
          color: "text.secondary",
          fontSize: { xs: "1rem", md: "1.3rem" },
          lineHeight: 1.7,
          whiteSpace: "pre-line",
          display: "block",
          width: "100%",
          textAlign: "center",
          mb: 5,
        }}
      >
        {subtitle}
      </Typography>

      <Grid container spacing={2}>
        {displayedLanguages.map((language, index) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
            <Card
              sx={{
                ...styles.freshBox,
                ...styles.softBoxShadow,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 3,
                },
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  p: 1,
                  "&:last-child": { pb: 1 },
                }}
              >
                <Box sx={{ mb: 1.5 }}>
                  <ReactCountryFlag
                    countryCode={language.flag.toUpperCase()}
                    svg
                    style={{
                      width: "42px",
                      height: "auto",
                      borderRadius: "4px",
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.85rem", sm: "1rem" },
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  {language.name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Show more button on mobile */}
        {hasMoreLanguages && !showAll && (
          <Grid item xs={12} sm={4} md={3} lg={2}>
            <Card
              sx={{
                ...styles.freshBox,
                ...styles.softBoxShadow,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 3,
                },
                border: "1px dashed",
                borderColor: "primary.main",
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              }}
              onClick={() => setShowAll(true)}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  p: 1,
                  "&:last-child": { pb: 1 },
                }}
              >
                <Box sx={{}}>
                  <Typography
                    sx={{
                      fontSize: "2rem",
                      color: "primary.main",
                    }}
                  >
                    +
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    fontWeight: 600,
                    color: "text.primary",
                    lineHeight: 1.5,
                  }}
                >
                  Visa fler språk
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.85rem",
                    color: "text.secondary",
                    mt: 0.5,
                  }}
                >
                  +{languages.length - MOBILE_LIMIT} språk
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Show less button when all languages are displayed on mobile */}
      {hasMoreLanguages && showAll && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setShowAll(false)}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            Visa färre språk
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default OurLanguages;
